/**
 * MentionsList — every place this person was named, newest first (2026-09-17).
 *
 * The catch-up view. A row says who named you, in which thread, when, and what
 * they said; pressing it opens that thread and lands on that message through the
 * same scroll-and-glow a search hit rides. Around each one it shows the message
 * before and the message after, so the point of the mention is legible without
 * opening anything — which is what Miles asked for ("a message before, a message
 * after… so they kinda understand it, caught up quickly, all in one view").
 *
 * THE CONTEXT IS READ LIVE, IN ONE QUERY PER THREAD. It cannot be stored on the
 * row: at the instant a mention is filed the messages after it do not exist yet,
 * and a stored "before" goes stale. The host plans the windows
 * (`planContextWindows`) and hands this component whatever came back, so a
 * thirty-row list costs a handful of reads rather than thirty — and a window that
 * fails degrades to the row's own stored snippet, never to a spinner that never
 * resolves.
 *
 * PEOPLE FIRST. The default filter hides Prest0n's own mentions: he addresses the
 * user in nearly every reply, so an unfiltered list is almost entirely his and
 * buries exactly what this exists to surface. `all` is one press away.
 *
 * Copy is chrome in the viewer's language (D9) through `threadListCopy`. The
 * snippets are other people's words, verbatim, rendered as TEXT — never as html.
 */
import React from 'react';
import styled from 'styled-components';
import { threadListCopy } from './threadListCopy';
import type { ThreadListLang } from '../../types/threadCategories';
import type { MentionReadState, ThreadMention } from '../../types/threadMention';
import { isMentionUnread } from '../../types/threadMention';
import type { MentionContextMessage, MentionFilter } from '../../services/threadMentions/mentionInboxModel';

export interface MentionRowContext {
  before: MentionContextMessage[];
  after: MentionContextMessage[];
}

export interface MentionsListProps {
  rows: readonly ThreadMention[];
  readState: MentionReadState;
  filter: MentionFilter;
  onFilterChange: (filter: MentionFilter) => void;
  /** Open the thread and land on the message. */
  onOpen: (row: ThreadMention) => void;
  onMarkAllRead: () => void;
  /** Context by anchor id — absent while a window is still in flight, or after it failed. */
  contextByMentionId?: Readonly<Record<string, MentionRowContext>>;
  /** Which rows are expanded to the wider radius. */
  expandedIds?: ReadonlySet<string>;
  onToggleExpanded?: (id: string) => void;
  /** Relative time, the rail's own formatter — passed in so this file holds no clock. */
  formatWhen: (orderTs: number) => string;
  lang?: ThreadListLang;
}

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 12px 6px 12px;
`;

const FilterGroup = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 2px;
  border: 1px solid rgba(198, 167, 94, 0.2);
  border-radius: 7px;
`;

const FilterButton = styled.button<{ $active: boolean }>`
  padding: 2px 8px;
  border: none;
  border-radius: 5px;
  background: ${(p) => (p.$active ? 'rgba(198, 167, 94, 0.2)' : 'transparent')};
  color: ${(p) => (p.$active ? 'var(--brand-gold)' : 'rgba(255, 255, 255, 0.55)')};
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11px;
  font-weight: ${(p) => (p.$active ? 600 : 500)};
  cursor: pointer;

  &:hover { color: var(--brand-gold); }
  &:focus-visible { outline: 2px solid rgba(198, 167, 94, 0.6); outline-offset: 1px; }
`;

/** The quiet lowercase ghost button the rail's section headers already use. */
const GhostAction = styled.button`
  margin-left: auto;
  padding: 2px 7px;
  border: none;
  border-radius: 6px;
  background: transparent;
  color: rgba(182, 146, 76, 0.85);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 0.68rem;
  cursor: pointer;

  &:hover { background: rgba(198, 167, 94, 0.08); color: var(--brand-gold); }
  &:focus-visible { outline: 2px solid rgba(198, 167, 94, 0.6); outline-offset: 1px; }
`;

const List = styled.div`
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  padding: 0 8px 12px;
  scrollbar-width: thin;
  scrollbar-color: rgba(198, 167, 94, 0.25) transparent;
`;

const Row = styled.div<{ $unread: boolean }>`
  margin: 0 0 6px;
  /* The whole colour rides the interpolation, never a hardcoded channel triple
     with an interpolated alpha — the design-token gate reads the latter as a
     palette value escaping src/styles/theme.ts. */
  border: 1px solid ${(p) => (p.$unread ? 'rgba(198, 167, 94, 0.3)' : 'rgba(198, 167, 94, 0.14)')};
  border-left: 3px solid ${(p) => (p.$unread ? 'var(--brand-gold)' : 'rgba(198, 167, 94, 0.2)')};
  border-radius: 8px;
  background: ${(p) => (p.$unread ? 'rgba(198, 167, 94, 0.08)' : 'rgba(198, 167, 94, 0.03)')};
`;

const RowButton = styled.button`
  display: block;
  width: 100%;
  box-sizing: border-box;
  padding: 7px 10px 6px;
  border: none;
  border-radius: 8px;
  background: transparent;
  text-align: left;
  cursor: pointer;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;

  &:hover { background: rgba(198, 167, 94, 0.07); }
  &:focus-visible { outline: 2px solid rgba(198, 167, 94, 0.6); outline-offset: -2px; }
`;

const RowHead = styled.div`
  display: flex;
  align-items: baseline;
  gap: 6px;
  margin-bottom: 3px;
  color: var(--brand-gold);
  font-size: 11.5px;
  font-weight: 600;
`;

const RowWhere = styled.span`
  flex: 1 1 auto;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  color: rgba(255, 255, 255, 0.5);
  font-size: 10.5px;
  font-weight: 500;
`;

const RowWhen = styled.span`
  flex: 0 0 auto;
  color: rgba(255, 255, 255, 0.4);
  font-size: 10.5px;
  font-weight: 500;
`;

const Snippet = styled.div`
  color: rgba(255, 255, 255, 0.88);
  font-size: 12px;
  line-height: 1.4;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
`;

const ContextLine = styled.div`
  padding: 2px 10px;
  color: rgba(255, 255, 255, 0.45);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11px;
  line-height: 1.35;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
`;

const ContextWho = styled.span`
  color: rgba(255, 255, 255, 0.62);
  font-weight: 600;
`;

const ExpandButton = styled.button`
  display: block;
  width: 100%;
  padding: 3px 10px 6px;
  border: none;
  background: transparent;
  color: rgba(182, 146, 76, 0.8);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 10.5px;
  text-align: left;
  cursor: pointer;

  &:hover { color: var(--brand-gold); }
  &:focus-visible { outline: 2px solid rgba(198, 167, 94, 0.6); outline-offset: -2px; }
`;

const Empty = styled.div`
  padding: 18px 14px;
  color: rgba(255, 255, 255, 0.45);
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
  font-size: 11.5px;
  text-align: center;
  line-height: 1.5;
`;

function contextLine(m: MentionContextMessage, key: string): React.ReactElement {
  return (
    <ContextLine key={key} data-mention-context={key}>
      {m.senderName ? <ContextWho>{m.senderName}: </ContextWho> : null}
      {m.text}
    </ContextLine>
  );
}

export const MentionsList: React.FC<MentionsListProps> = ({
  rows,
  readState,
  filter,
  onFilterChange,
  onOpen,
  onMarkAllRead,
  contextByMentionId,
  expandedIds,
  onToggleExpanded,
  formatWhen,
  lang = 'en',
}) => {
  const copy = threadListCopy(lang);
  const anyUnread = rows.some((row) => isMentionUnread(row, readState));

  return (
    <Wrap data-mentions-list="">
      <HeaderRow>
        <FilterGroup role="group" aria-label={copy.mentionsFilterLabel}>
          <FilterButton
            type="button"
            data-mentions-filter="people"
            $active={filter === 'people'}
            aria-pressed={filter === 'people'}
            onClick={() => onFilterChange('people')}
          >
            {copy.mentionsFromPeople}
          </FilterButton>
          <FilterButton
            type="button"
            data-mentions-filter="all"
            $active={filter === 'all'}
            aria-pressed={filter === 'all'}
            onClick={() => onFilterChange('all')}
          >
            {copy.mentionsFromAll}
          </FilterButton>
        </FilterGroup>
        {anyUnread && (
          <GhostAction type="button" data-mentions-mark-all="" onClick={onMarkAllRead}>
            {copy.markAllRead}
          </GhostAction>
        )}
      </HeaderRow>

      <List>
        {rows.length === 0 && (
          <Empty data-mentions-empty="">{copy.noMentionsYet}</Empty>
        )}
        {rows.map((row) => {
          const unread = isMentionUnread(row, readState);
          const who = row.byPrest0n ? copy.prest0n : (row.senderName || copy.prest0n);
          const headline = row.reason === 'reply' ? copy.repliedToYou(who) : copy.mentionedYou(who);
          const ctx = contextByMentionId?.[row.id];
          const expanded = expandedIds?.has(row.id) === true;
          const hasContext = Boolean(ctx && (ctx.before.length > 0 || ctx.after.length > 0));
          return (
            <Row
              key={row.id}
              $unread={unread}
              data-mention-row={row.id}
              data-mention-unread={unread ? 'true' : 'false'}
              data-mention-reason={row.reason}
            >
              {ctx?.before.map((m, i) => contextLine(m, `before-${row.id}-${m.id || i}`))}
              <RowButton
                type="button"
                aria-label={copy.openMention(who)}
                onClick={() => onOpen(row)}
              >
                <RowHead>
                  <span>{headline}</span>
                  <RowWhere>{row.threadTitle}</RowWhere>
                  <RowWhen>{formatWhen(row.orderTs)}</RowWhen>
                </RowHead>
                <Snippet>{row.snippet}</Snippet>
              </RowButton>
              {ctx?.after.map((m, i) => contextLine(m, `after-${row.id}-${m.id || i}`))}
              {onToggleExpanded && hasContext && (
                <ExpandButton
                  type="button"
                  data-mention-expand={row.id}
                  aria-expanded={expanded}
                  onClick={() => onToggleExpanded(row.id)}
                >
                  {expanded ? copy.hideContext : copy.showContext}
                </ExpandButton>
              )}
            </Row>
          );
        })}
      </List>
    </Wrap>
  );
};

export default MentionsList;
